import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { environment } from '../../environments/environment';

/**
 * Servicio de autenticación para Azure AD usando MSAL.js
 * Maneja el flujo de autenticación OAuth 2.0 con Azure AD
 */
@Injectable({
  providedIn: 'root',
})
export class AzureAuthService {
  private msalInstance: any;
  private currentUserSubject: BehaviorSubject<any>;
  private JWTSubject: BehaviorSubject<any>;

  public currentUser: Observable<any>;
  public JWT: Observable<any>;
  public idToken: string = '';

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
  ) {
    // Inicializar con datos de localStorage
    const currentUserData = this.getSafeLocalStorageData('currentUser');
    this.currentUserSubject = new BehaviorSubject<any>(currentUserData);

    const jwtData = this.getSafeLocalStorageData('JWT');
    this.JWTSubject = new BehaviorSubject<any>(jwtData);

    this.currentUser = this.currentUserSubject.asObservable();
    this.JWT = this.JWTSubject.asObservable();

    // Inicializar MSAL
    this.initializeMSAL();
  }

  /**
   * Inicializa la instancia de MSAL con la configuración de Azure AD
   */
  private async initializeMSAL(): Promise<void> {
    try {
      // Importar MSAL dinámicamente
      const msal = await import('@azure/msal-browser');

      const msalConfig = {
        auth: {
          clientId: environment.azureAd.clientId,
          authority: `https://login.microsoftonline.com/${environment.azureAd.tenantId}`,
          redirectUri: environment.azureAd.redirectUri,
          postLogoutRedirectUri: environment.azureAd.postLogoutRedirectUri,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false,
        },
      };

      this.msalInstance = new msal.PublicClientApplication(msalConfig);
      await this.msalInstance.initialize();

      // Manejar la respuesta de redirección
      await this.handleRedirectResponse();
    } catch (error) {
      console.error('Error inicializando MSAL:', error);
    }
  }

  /**
   * Maneja la respuesta de redirección después del login
   */
  private async handleRedirectResponse(): Promise<void> {
    try {
      const response = await this.msalInstance.handleRedirectPromise();

      if (response) {
        console.log('Respuesta de autenticación recibida:', response);
        await this.handleAuthenticationResponse(response);
      }
    } catch (error) {
      console.error('Error manejando respuesta de redirección:', error);
    }
  }

  /**
   * Inicia el flujo de autenticación con Azure AD
   */
  async login(): Promise<void> {
    try {
      const loginRequest = {
        scopes: environment.azureAd.scopes,
        prompt: 'select_account',
      };

      // Usar popup o redirect según configuración
      if (environment.azureAd.usePopup) {
        const response = await this.msalInstance.loginPopup(loginRequest);
        await this.handleAuthenticationResponse(response);
      } else {
        await this.msalInstance.loginRedirect(loginRequest);
      }
    } catch (error) {
      console.error('Error durante el login:', error);
      throw error;
    }
  }

  /**
   * Procesa la respuesta de autenticación y guarda los datos del usuario
   */
  private async handleAuthenticationResponse(response: any): Promise<void> {
    try {
      if (!response || !response.idToken) {
        throw new Error('No se recibió token de autenticación');
      }

      // Guardar el token
      this.idToken = response.idToken;
      this.localStorageService.saveData('JWT', response.idToken);
      this.JWTSubject.next(response.idToken);

      // Procesar información del usuario
      const account = response.account || this.msalInstance.getAllAccounts()[0];

      if (account) {
        const userInfo = {
          email: account.username,
          name: account.name,
          idTokenClaims: account.idTokenClaims,
          exp: account.idTokenClaims?.exp,
        };

        this.localStorageService.saveData(
          'currentUser',
          JSON.stringify(userInfo),
        );
        this.localStorageService.saveData('userEmail', account.username);
        this.currentUserSubject.next(userInfo);
        console.log('Usuario autenticado:', userInfo);

        // 🌍 Pre-obtener ubicación del usuario
        try {
          const usageLocation = await this.getUsageLocation();
          if (usageLocation) {
            this.localStorageService.saveData(
              'user_usage_location',
              usageLocation,
            );
          }
        } catch (locationError) {
          console.warn('No se pudo pre-obtener la ubicación:', locationError);
        }

        // Redirigir al chat
        await this.router.navigate(['/app/chat']);
      }
    } catch (error) {
      console.error('Error procesando respuesta de autenticación:', error);
      throw error;
    }
  }

  /**
   * Obtiene un token de acceso válido (renovándolo si es necesario)
   */
  async getAccessToken(): Promise<string> {
    try {
      const accounts = this.msalInstance.getAllAccounts();

      if (accounts.length === 0) {
        throw new Error('No hay cuentas autenticadas');
      }

      const request = {
        scopes: environment.azureAd.scopes,
        account: accounts[0],
      };

      // Intentar obtener token silenciosamente
      const response = await this.msalInstance.acquireTokenSilent(request);
      return response.idToken;
    } catch (error) {
      console.error('Error obteniendo token:', error);

      // Si falla, intentar con popup/redirect
      try {
        const request = {
          scopes: environment.azureAd.scopes,
        };

        if (environment.azureAd.usePopup) {
          const response = await this.msalInstance.acquireTokenPopup(request);
          return response.idToken;
        } else {
          await this.msalInstance.acquireTokenRedirect(request);
          throw new Error('Redirigiendo para renovar token');
        }
      } catch (renewError) {
        console.error('Error renovando token:', renewError);
        throw renewError;
      }
    }
  }

  /**
   * Obtiene la ubicación de uso (usageLocation) del usuario desde Microsoft Graph API
   * Incluye retry y timeout para mayor robustez en navegador incógnito
   * @returns Código ISO del país (ej: 'MX', 'BR', 'US') o null si no está disponible
   */
  async getUsageLocation(): Promise<string | null> {
    const MAX_RETRIES = 2;
    const TIMEOUT_MS = 5000;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const accounts = this.msalInstance.getAllAccounts();

        if (accounts.length === 0) {
          console.warn(
            '📍 No hay cuentas autenticadas para obtener usageLocation',
          );
          return null;
        }

        // Obtener token con scope User.Read para Graph API
        const request = {
          scopes: ['User.Read'],
          account: accounts[0],
        };

        console.log(
          `📍 Obteniendo token para Graph API (intento ${attempt}/${MAX_RETRIES})...`,
        );
        const tokenResponse =
          await this.msalInstance.acquireTokenSilent(request);
        const accessToken = tokenResponse.accessToken;

        // Consultar Microsoft Graph API con timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

        console.log(
          `📍 Consultando Graph API (intento ${attempt}/${MAX_RETRIES})...`,
        );
        const graphResponse = await fetch(
          'https://graph.microsoft.com/v1.0/me?$select=usageLocation',
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            signal: controller.signal,
          },
        );
        clearTimeout(timeoutId);

        if (!graphResponse.ok) {
          console.error(
            `📍 Error consultando Graph API (intento ${attempt}):`,
            graphResponse.status,
          );
          if (attempt < MAX_RETRIES) {
            console.log('📍 Reintentando...');
            continue;
          }
          return null;
        }

        const userData = await graphResponse.json();
        console.log('📊 Datos de Graph API:', userData);
        const usageLocation = userData.usageLocation || null;

        if (usageLocation) {
          console.log('📍 usageLocation obtenido exitosamente:', usageLocation);
        } else {
          console.warn(
            '📍 El usuario no tiene usageLocation configurado en Azure AD',
          );
        }

        return usageLocation;
      } catch (error: any) {
        const isTimeout = error?.name === 'AbortError';
        const errorType = isTimeout ? 'Timeout' : 'Error';

        console.error(
          `📍 ${errorType} obteniendo usageLocation (intento ${attempt}/${MAX_RETRIES}):`,
          error?.message || error,
        );

        if (attempt >= MAX_RETRIES) {
          console.warn(
            '📍 Se agotaron los reintentos para obtener usageLocation',
          );
          return null;
        }
        console.log('📍 Reintentando...');
      }
    }

    return null;
  }

  /**
   * Cierra la sesión del usuario con timeout de seguridad
   */
  async signOut(): Promise<void> {
    console.log('Iniciando cierre de sesión...');

    const LOGOUT_TIMEOUT_MS = 3000; // 3 segundos máximo

    try {
      const accounts = this.msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        const logoutRequest = {
          account: accounts[0],
          postLogoutRedirectUri: environment.azureAd.postLogoutRedirectUri,
        };

        // Race entre logoutRedirect y timeout de seguridad
        await Promise.race([
          this.msalInstance.logoutRedirect(logoutRequest),
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error('Timeout: logout excedió tiempo límite')),
              LOGOUT_TIMEOUT_MS,
            ),
          ),
        ]);
      }
    } catch (error) {
      // El error puede ser un timeout o un fallo real de MSAL
      // En cualquier caso, continuamos con la limpieza local
      console.warn('Logout de MSAL falló o excedió timeout:', error);
    } finally {
      // SIEMPRE limpiar datos locales, independientemente del resultado
      this.clearUserData();
      console.log('Cierre de sesión completado (limpieza local garantizada)');
    }
  }

  /**
   * Limpia todos los datos del usuario
   */
  private clearUserData(): void {
    try {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('JWT');
      localStorage.removeItem('userEmail');

      this.currentUserSubject.next(null);
      this.JWTSubject.next(null);
      this.idToken = '';

      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error limpiando datos del usuario:', error);
      this.router.navigate(['/login']);
    }
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isLogged(): boolean {
    const accounts = this.msalInstance?.getAllAccounts() || [];
    return accounts.length > 0 && !!this.currentUserValue;
  }

  /**
   * Verifica si la sesión está activa
   */
  isSessionActive(): boolean {
    try {
      const user = this.currentUserValue;
      if (user && user.exp) {
        const expDate = new Date(user.exp * 1000);
        const now = new Date();
        return expDate > now;
      }
      return false;
    } catch (error) {
      console.error('Error verificando sesión:', error);
      return false;
    }
  }

  /**
   * Verifica si el token es válido
   */
  isTokenValid(): boolean {
    try {
      const jwt = this.JWTValue;
      if (!jwt) return false;

      const payload = this.parseJWTPayload(jwt);
      if (payload && payload.exp) {
        const expDate = new Date(payload.exp * 1000);
        return expDate > new Date();
      }

      return false;
    } catch (error) {
      console.error('Error validando token:', error);
      return false;
    }
  }

  /**
   * Verifica autenticación completa
   */
  isFullyAuthenticated(): boolean {
    return this.isLogged() && this.isSessionActive() && this.isTokenValid();
  }

  // Getters
  public get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  public get JWTValue(): any {
    return this.JWTSubject.value;
  }

  /**
   * Método helper para obtener datos de localStorage de forma segura
   */
  private getSafeLocalStorageData(key: string): any {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error(`Error parsing localStorage data for key ${key}:`, error);
      return null;
    }
  }

  /**
   * Helper para parsear el payload de un JWT
   */
  private parseJWTPayload(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT payload:', error);
      return null;
    }
  }

  /**
   * Limpia todos los datos
   */
  clearAllData(): void {
    try {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('JWT');
      localStorage.removeItem('userEmail');

      this.currentUserSubject.next(null);
      this.JWTSubject.next(null);
      this.idToken = '';

      console.log('Datos de usuario limpiados completamente');
    } catch (error) {
      console.error('Error clearing all data:', error);
    }
  }
}
